import { useState, type FormEvent } from 'react';
import {
  useConsumables,
  useCreateConsumable,
  useCreateKit,
  useCreateTool,
  useDeleteConsumable,
  useDeleteKit,
  useDeleteTool,
  useKits,
  useLowStock,
  usePatchConsumable,
  useTools,
  useZones,
} from '@/lib/queries';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Plus, Trash2, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { Consumable, Tool } from '@/lib/types';
import { cn } from '@/lib/utils';
import PullToRefresh from '@/components/PullToRefresh';
import { useQueryClient } from '@tanstack/react-query';

export default function Inventory() {
  const queryClient = useQueryClient();

  const handleRefresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['tools'] });
    await queryClient.invalidateQueries({ queryKey: ['consumables'] });
    await queryClient.invalidateQueries({ queryKey: ['kits'] });
  };

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="space-y-6">
        <header className="space-y-2">
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
            Capability infrastructure
          </p>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">
            Inventory
          </h1>
          <p className="text-muted-foreground max-w-2xl">
            Tools, consumables, and operational kits. Low-stock surfaces every consumable below its
            minimum quantity.
          </p>
        </header>

        <Tabs defaultValue="tools">
          <TabsList className="overflow-x-auto -mx-1 px-1 max-w-full whitespace-nowrap">
            <TabsTrigger value="tools" data-testid="inv-tab-tools">Tools</TabsTrigger>
            <TabsTrigger value="consumables" data-testid="inv-tab-consumables">Consumables</TabsTrigger>
            <TabsTrigger value="low-stock" data-testid="inv-tab-low-stock">Low stock</TabsTrigger>
            <TabsTrigger value="kits" data-testid="inv-tab-kits">Kits</TabsTrigger>
          </TabsList>

          <TabsContent value="tools"><ToolsTab /></TabsContent>
          <TabsContent value="consumables"><ConsumablesTab /></TabsContent>
          <TabsContent value="low-stock"><LowStockTab /></TabsContent>
          <TabsContent value="kits"><KitsTab /></TabsContent>
        </Tabs>
      </div>
    </PullToRefresh>
  );
}

function ToolsTab() {
  const q = useTools();
  const del = useDeleteTool();
  const [creating, setCreating] = useState(false);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end">
        <Button onClick={() => setCreating(true)} data-testid="new-tool">
          <Plus className="h-4 w-4 mr-2" aria-hidden /> New tool
        </Button>
      </div>
      {q.isLoading ? <p className="text-sm text-muted-foreground">Loading…</p> : null}
      {q.data && q.data.length === 0 ? (
        <p className="text-sm text-muted-foreground">No tools yet.</p>
      ) : null}
      {q.data && q.data.length > 0 ? (
        <ul className="divide-y divide-rule rounded-md border border-rule bg-card" data-testid="tools-list">
          {q.data.map((t) => (
            <ToolRow key={t.id} tool={t} onDelete={() => del.mutateAsync(t.id)} />
          ))}
        </ul>
      ) : null}
      <ToolFormSheet open={creating} onOpenChange={setCreating} />
    </div>
  );
}

function ToolRow({ tool, onDelete }: { tool: Tool; onDelete: () => Promise<unknown> }) {
  return (
    <li className="flex items-start gap-3 px-4 py-3" data-testid={`tool-row-${tool.id}`}>
      <div className="flex-1 min-w-0 space-y-0.5">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          {tool.category}
        </p>
        <p className="font-display text-base font-medium leading-tight">{tool.name}</p>
        <p className="font-mono text-xs text-muted-foreground">{tool.status}</p>
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="text-destructive hover:text-destructive"
        onClick={onDelete}
        aria-label="Delete tool"
      >
        <Trash2 className="h-4 w-4" aria-hidden />
      </Button>
    </li>
  );
}

function ToolFormSheet({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
}) {
  const create = useCreateTool();
  const zonesQ = useZones();
  const [name, setName] = useState('');
  const [category, setCategory] = useState('measurement-layout');
  const [storageZoneId, setStorageZoneId] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Name required.');
      return;
    }
    try {
      await create.mutateAsync({
        name: name.trim(),
        category: category.trim() || 'misc',
        storageZoneId: storageZoneId || null,
      });
      setName('');
      setStorageZoneId('');
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.');
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom">
        <SheetHeader>
          <SheetTitle>New tool</SheetTitle>
        </SheetHeader>
        <form onSubmit={onSubmit} className="space-y-4 pt-4 pb-2" data-testid="tool-form">
          <div className="space-y-1.5">
            <Label className="font-mono text-[11px] uppercase tracking-wider">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required autoFocus className="h-11" data-testid="tool-name" />
          </div>
          <div className="space-y-1.5">
            <Label className="font-mono text-[11px] uppercase tracking-wider">Category</Label>
            <Input value={category} onChange={(e) => setCategory(e.target.value)} className="h-11" />
          </div>
          <div className="space-y-1.5">
            <Label className="font-mono text-[11px] uppercase tracking-wider">Storage</Label>
            <select
              value={storageZoneId}
              onChange={(e) => setStorageZoneId(e.target.value)}
              className="h-11 w-full rounded-md border border-input bg-background px-3 text-base focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">No zone</option>
              {(zonesQ.data ?? []).map((z) => (
                <option key={z.id} value={z.id}>
                  {z.name}
                </option>
              ))}
            </select>
          </div>
          {error ? <p className="text-sm text-warning">{error}</p> : null}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending} data-testid="tool-submit">
              {create.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function ConsumablesTab() {
  const q = useConsumables();
  const del = useDeleteConsumable();
  const [creating, setCreating] = useState(false);
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end">
        <Button onClick={() => setCreating(true)} data-testid="new-consumable">
          <Plus className="h-4 w-4 mr-2" aria-hidden /> New consumable
        </Button>
      </div>
      {q.isLoading ? <p className="text-sm text-muted-foreground">Loading…</p> : null}
      {q.data && q.data.length === 0 ? (
        <p className="text-sm text-muted-foreground">No consumables yet.</p>
      ) : null}
      {q.data && q.data.length > 0 ? (
        <ul className="divide-y divide-rule rounded-md border border-rule bg-card" data-testid="consumables-list">
          {q.data.map((c) => (
            <ConsumableRow key={c.id} consumable={c} onDelete={() => del.mutateAsync(c.id)} />
          ))}
        </ul>
      ) : null}
      <ConsumableFormSheet open={creating} onOpenChange={setCreating} />
    </div>
  );
}

function ConsumableRow({
  consumable,
  onDelete,
}: {
  consumable: Consumable;
  onDelete: () => Promise<unknown>;
}) {
  const patch = usePatchConsumable();
  const low =
    consumable.minimumQuantity != null &&
    consumable.quantity != null &&
    consumable.quantity < consumable.minimumQuantity;
  return (
    <li
      className={cn('flex items-start gap-3 px-4 py-3', low && 'bg-warning/10')}
      data-testid={`consumable-row-${consumable.id}`}
    >
      <div className="flex-1 min-w-0 space-y-0.5">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          {consumable.category}
        </p>
        <p className="font-display text-base font-medium leading-tight">{consumable.name}</p>
        <p className="font-mono text-xs text-muted-foreground tabular-nums">
          {consumable.quantity ?? 0}
          {consumable.unit ? ` ${consumable.unit}` : ''}
          {consumable.minimumQuantity != null
            ? ` · min ${consumable.minimumQuantity}${consumable.unit ? ` ${consumable.unit}` : ''}`
            : ''}
        </p>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Button
          variant="ghost"
          size="icon"
          onClick={() =>
            patch.mutateAsync({
              id: consumable.id,
              patch: { quantity: Math.max(0, (consumable.quantity ?? 0) - 1) },
            })
          }
          aria-label="Decrement"
        >
          −
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() =>
            patch.mutateAsync({
              id: consumable.id,
              patch: { quantity: (consumable.quantity ?? 0) + 1 },
            })
          }
          aria-label="Increment"
          data-testid={`consumable-${consumable.id}-inc`}
        >
          +
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="text-destructive hover:text-destructive"
          onClick={onDelete}
          aria-label="Delete consumable"
        >
          <Trash2 className="h-4 w-4" aria-hidden />
        </Button>
      </div>
    </li>
  );
}

function ConsumableFormSheet({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
}) {
  const create = useCreateConsumable();
  const zonesQ = useZones();
  const [name, setName] = useState('');
  const [category, setCategory] = useState('fasteners');
  const [quantity, setQuantity] = useState('');
  const [minimumQuantity, setMinimumQuantity] = useState('');
  const [unit, setUnit] = useState('');
  const [storageZoneId, setStorageZoneId] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Name required.');
      return;
    }
    try {
      await create.mutateAsync({
        name: name.trim(),
        category: category.trim() || 'misc',
        quantity: quantity.trim() === '' ? null : Number.parseInt(quantity, 10),
        minimumQuantity:
          minimumQuantity.trim() === '' ? null : Number.parseInt(minimumQuantity, 10),
        unit: unit.trim() || null,
        storageZoneId: storageZoneId || null,
      });
      setName('');
      setQuantity('');
      setMinimumQuantity('');
      setUnit('');
      setStorageZoneId('');
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.');
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom">
        <SheetHeader>
          <SheetTitle>New consumable</SheetTitle>
        </SheetHeader>
        <form onSubmit={onSubmit} className="space-y-4 pt-4 pb-2" data-testid="consumable-form">
          <div className="space-y-1.5">
            <Label className="font-mono text-[11px] uppercase tracking-wider">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required autoFocus className="h-11" data-testid="consumable-name" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="font-mono text-[11px] uppercase tracking-wider">Qty</Label>
              <Input type="number" inputMode="numeric" min={0} value={quantity} onChange={(e) => setQuantity(e.target.value)} className="h-11 font-tabular" data-testid="consumable-qty" />
            </div>
            <div className="space-y-1.5">
              <Label className="font-mono text-[11px] uppercase tracking-wider">Min</Label>
              <Input type="number" inputMode="numeric" min={0} value={minimumQuantity} onChange={(e) => setMinimumQuantity(e.target.value)} className="h-11 font-tabular" data-testid="consumable-min" />
            </div>
            <div className="space-y-1.5">
              <Label className="font-mono text-[11px] uppercase tracking-wider">Unit</Label>
              <Input value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="box / pkg / oz" className="h-11" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="font-mono text-[11px] uppercase tracking-wider">Category</Label>
            <Input value={category} onChange={(e) => setCategory(e.target.value)} className="h-11" />
          </div>
          <div className="space-y-1.5">
            <Label className="font-mono text-[11px] uppercase tracking-wider">Storage</Label>
            <select
              value={storageZoneId}
              onChange={(e) => setStorageZoneId(e.target.value)}
              className="h-11 w-full rounded-md border border-input bg-background px-3 text-base focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">No zone</option>
              {(zonesQ.data ?? []).map((z) => (
                <option key={z.id} value={z.id}>
                  {z.name}
                </option>
              ))}
            </select>
          </div>
          {error ? <p className="text-sm text-warning">{error}</p> : null}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending} data-testid="consumable-submit">
              {create.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function LowStockTab() {
  const q = useLowStock();
  if (q.isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (!q.data || q.data.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        Nothing under minimum quantity. Set a `min` on a consumable to track it here.
      </p>
    );
  }
  return (
    <ul className="divide-y divide-rule rounded-md border border-rule bg-card" data-testid="low-stock-list">
      {q.data.map((c) => (
        <li key={c.id} className="flex items-start gap-3 px-4 py-3 bg-warning/10">
          <AlertTriangle className="h-4 w-4 text-warning mt-1" aria-hidden />
          <div className="flex-1 min-w-0 space-y-0.5">
            <p className="font-display text-base font-medium leading-tight">{c.name}</p>
            <p className="font-mono text-xs text-muted-foreground tabular-nums">
              {c.quantity ?? 0}
              {c.unit ? ` ${c.unit}` : ''} · need ≥ {c.minimumQuantity}
            </p>
          </div>
        </li>
      ))}
    </ul>
  );
}

function KitsTab() {
  const q = useKits();
  const create = useCreateKit();
  const del = useDeleteKit();
  const [creating, setCreating] = useState(false);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end">
        <Button onClick={() => setCreating(true)} data-testid="new-kit">
          <Plus className="h-4 w-4 mr-2" aria-hidden /> New kit
        </Button>
      </div>
      {q.isLoading ? <p className="text-sm text-muted-foreground">Loading…</p> : null}
      {q.data && q.data.length === 0 ? (
        <p className="text-sm text-muted-foreground">No kits yet.</p>
      ) : null}
      {q.data && q.data.length > 0 ? (
        <ul className="grid gap-3 md:grid-cols-2">
          {q.data.map((k) => (
            <li key={k.id}>
              <Card>
                <CardHeader className="pb-2 space-y-1.5">
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                    {k.slug}
                  </p>
                  <CardTitle className="font-display text-base">{k.name}</CardTitle>
                </CardHeader>
                <CardContent className="pt-0 space-y-2">
                  {k.description ? (
                    <p className="text-sm text-muted-foreground line-clamp-2">{k.description}</p>
                  ) : null}
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/inventory/kits/${k.id}`}
                      className="font-mono text-[11px] uppercase tracking-wider text-primary hover:underline"
                    >
                      Open →
                    </Link>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => del.mutateAsync(k.id)}
                      aria-label="Delete kit"
                      className="ml-auto text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" aria-hidden />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      ) : null}
      <KitFormSheet
        open={creating}
        onOpenChange={setCreating}
        onCreate={async (body) => {
          await create.mutateAsync(body);
        }}
      />
    </div>
  );
}

function KitFormSheet({
  open,
  onOpenChange,
  onCreate,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  onCreate: (body: { name: string; slug: string; description: string | null }) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (!name.trim() || !slug.trim()) {
      setError('Name and slug are required.');
      return;
    }
    try {
      await onCreate({
        name: name.trim(),
        slug: slug.trim().toLowerCase(),
        description: description.trim() || null,
      });
      setName('');
      setSlug('');
      setDescription('');
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.');
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom">
        <SheetHeader>
          <SheetTitle>New kit</SheetTitle>
        </SheetHeader>
        <form onSubmit={onSubmit} className="space-y-4 pt-4 pb-2" data-testid="kit-form">
          <div className="space-y-1.5">
            <Label className="font-mono text-[11px] uppercase tracking-wider">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required autoFocus className="h-11" data-testid="kit-name" />
          </div>
          <div className="space-y-1.5">
            <Label className="font-mono text-[11px] uppercase tracking-wider">Slug</Label>
            <Input
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="mower-startup-kit"
              required
              className="h-11 font-mono"
              data-testid="kit-slug"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="font-mono text-[11px] uppercase tracking-wider">Description</Label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-ring resize-y"
            />
          </div>
          {error ? <p className="text-sm text-warning">{error}</p> : null}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" data-testid="kit-submit">
              Save
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
